import { OFF_NUTRIENT_MAP, OFF_NUTRIENT_METADATA } from '../config/openFoodFactsNutrients';

const OFF_NUTRIMENT_FIELDS = Object.freeze(OFF_NUTRIENT_METADATA.map((nutrient) => nutrient.key));
const SCALABLE_UNITS = new Set(['g', 'mg', 'µg', 'kJ', 'kcal']);
const UNIT_ALIASES = Object.freeze({
  ug: 'µg',
  μg: 'µg',
  mcg: 'µg',
  kj: 'kJ',
  kcal: 'kcal',
  g: 'g',
  mg: 'mg',
  'µg': 'µg',
});
const MASS_UNIT_FACTORS_IN_GRAMS = Object.freeze({
  g: 1,
  mg: 1e-3,
  'µg': 1e-6,
});
const ENERGY_UNIT_FACTORS_IN_KJ = Object.freeze({
  kJ: 1,
  kcal: 4.184,
});

const unique = (values = []) => Array.from(new Set(values.filter(Boolean)));

const buildBaseKeyVariants = (baseKey = '') => {
  const normalized = String(baseKey || '').trim();
  if (!normalized) {
    return [];
  }

  const lower = normalized.toLowerCase();

  return unique([
    normalized,
    lower,
    normalized.replace(/\s+/g, '-'),
    lower.replace(/\s+/g, '-'),
    normalized.replace(/\s+/g, '_'),
    lower.replace(/\s+/g, '_'),
    normalized.replace(/-/g, '_'),
    lower.replace(/-/g, '_'),
    normalized.replace(/_/g, '-'),
    lower.replace(/_/g, '-'),
  ]);
};

const buildNutrimentCandidateKeys = (baseKey) => unique(
  buildBaseKeyVariants(baseKey).flatMap((variant) => ([
    `${variant}_100g`,
    variant,
    `${variant}_prepared_100g`,
    `${variant}_prepared`,
    `${variant}_value`,
    `${variant}_prepared_value`,
  ]))
);

const buildNutrimentUnitCandidateKeys = (baseKey) => unique(
  buildBaseKeyVariants(baseKey).flatMap((variant) => ([
    `${variant}_unit`,
    `${variant}_prepared_unit`,
  ]))
);

const OFF_NUTRIMENT_CANDIDATE_KEYS = Object.freeze(Object.fromEntries(
  OFF_NUTRIMENT_FIELDS.map((field) => [field, buildNutrimentCandidateKeys(field)])
));

export const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const readNutrimentValue = (nutriments = {}, keys = []) => {
  for (const key of keys) {
    const parsed = toFiniteNumber(nutriments[key]);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
};

const normalizeUnit = (unit = '') => {
  const normalizedUnit = String(unit || '').trim();
  if (!normalizedUnit) {
    return '';
  }

  return UNIT_ALIASES[normalizedUnit] || UNIT_ALIASES[normalizedUnit.toLowerCase()] || normalizedUnit;
};

export const readNutrimentUnit = (nutriments = {}, baseKey) => {
  const candidateKeys = buildNutrimentUnitCandidateKeys(baseKey);

  for (const key of candidateKeys) {
    const normalizedUnit = normalizeUnit(nutriments[key]);
    if (normalizedUnit) {
      return normalizedUnit;
    }
  }

  return '';
};

export const readNutrimentPer100g = (nutriments = {}, baseKey) => (
  readNutrimentValue(nutriments, OFF_NUTRIMENT_CANDIDATE_KEYS[baseKey] || buildNutrimentCandidateKeys(baseKey))
);

export const readEnergyKcalPer100g = (nutriments = {}) => {
  const kcalValue = readNutrimentValue(nutriments, [
    'energy-kcal_100g',
    'energy_kcal_100g',
    'energy-kcal',
    'energy_kcal',
    'energy-kcal_prepared_100g',
    'energy_kcal_prepared_100g',
    'energy-kcal_prepared',
    'energy_kcal_prepared',
    'energy-kcal_value',
    'energy_kcal_value',
    'energy-kcal_prepared_value',
    'energy_kcal_prepared_value',
  ]);

  if (kcalValue !== null) {
    return kcalValue;
  }

  const kjValue = readNutrimentValue(nutriments, [
    'energy-kj_100g',
    'energy_kj_100g',
    'energy-kj',
    'energy_kj',
    'energy-kj_prepared_100g',
    'energy_kj_prepared_100g',
    'energy-kj_prepared',
    'energy_kj_prepared',
    'energy-kj_value',
    'energy_kj_value',
    'energy-kj_prepared_value',
    'energy_kj_prepared_value',
    'energy_100g',
    'energy',
    'energy_prepared_100g',
    'energy_prepared',
    'energy_prepared_value',
  ]);

  return kjValue !== null ? kjValue / 4.184 : null;
};

const stripMissingNutritionValues = (nutrition = {}) => Object.fromEntries(
  Object.entries(nutrition).filter(([, value]) => value !== null && value !== undefined)
);

const convertUnitValue = (value, sourceUnit = '', targetUnit = '') => {
  if (value === null) {
    return null;
  }

  const normalizedSourceUnit = normalizeUnit(sourceUnit);
  const normalizedTargetUnit = normalizeUnit(targetUnit);

  if (!normalizedSourceUnit || !normalizedTargetUnit || normalizedSourceUnit === normalizedTargetUnit) {
    return value;
  }

  const sourceMassFactor = MASS_UNIT_FACTORS_IN_GRAMS[normalizedSourceUnit];
  const targetMassFactor = MASS_UNIT_FACTORS_IN_GRAMS[normalizedTargetUnit];
  if (sourceMassFactor && targetMassFactor) {
    return (value * sourceMassFactor) / targetMassFactor;
  }

  const sourceEnergyFactor = ENERGY_UNIT_FACTORS_IN_KJ[normalizedSourceUnit];
  const targetEnergyFactor = ENERGY_UNIT_FACTORS_IN_KJ[normalizedTargetUnit];
  if (sourceEnergyFactor && targetEnergyFactor) {
    return (value * sourceEnergyFactor) / targetEnergyFactor;
  }

  return value;
};

const shouldScaleUnit = (unit = '') => SCALABLE_UNITS.has(unit);

const scaleNutritionValue = (value, multiplier, unit = '') => {
  if (value === null) {
    return null;
  }

  if (!shouldScaleUnit(unit)) {
    return value;
  }

  const scaled = value * multiplier;
  return Number.isFinite(scaled) ? scaled : null;
};

export const buildNutrition100FromNutriments = (nutriments = {}) => {
  const nutrition100 = {
    'energy-kcal_100g': readEnergyKcalPer100g(nutriments),
  };

  OFF_NUTRIMENT_FIELDS.forEach((field) => {
    const rawValue = readNutrimentPer100g(nutriments, field);
    if (rawValue === null) {
      nutrition100[`${field}_100g`] = null;
      return;
    }

    const sourceUnit = readNutrimentUnit(nutriments, field);
    const targetUnit = OFF_NUTRIENT_MAP[field]?.unit || sourceUnit;
    nutrition100[`${field}_100g`] = convertUnitValue(rawValue, sourceUnit, targetUnit);
  });

  return stripMissingNutritionValues(nutrition100);
};

export const buildAdjustedNutritionForDisplay = (nutrition100 = {}, quantity = 100) => {
  const normalizedQuantity = Number(quantity);
  const effectiveQuantity = Number.isFinite(normalizedQuantity) && normalizedQuantity > 0
    ? normalizedQuantity
    : 100;
  const multiplier = effectiveQuantity / 100;
  const adjusted = {};

  const energyPer100g = toFiniteNumber(nutrition100['energy-kcal_100g']);
  if (energyPer100g !== null) {
    adjusted.calories = energyPer100g * multiplier;
    adjusted['energy-kcal'] = energyPer100g * multiplier;
    adjusted['energy-kcal_100g'] = energyPer100g;
  }

  OFF_NUTRIMENT_FIELDS.forEach((field) => {
    const sourceValue = toFiniteNumber(nutrition100[`${field}_100g`]);
    if (sourceValue === null) {
      return;
    }

    const unit = OFF_NUTRIENT_MAP[field]?.unit || '';
    adjusted[field] = scaleNutritionValue(sourceValue, multiplier, unit);
    adjusted[`${field}_100g`] = sourceValue;
  });

  Object.entries(nutrition100).forEach(([key, value]) => {
    if (Object.prototype.hasOwnProperty.call(adjusted, key)) {
      return;
    }

    const parsed = toFiniteNumber(value);
    if (parsed !== null) {
      adjusted[key] = parsed;
    }
  });

  return adjusted;
};

export const hasMacroData = (nutriments = {}) => {
  if (!nutriments || typeof nutriments !== 'object') {
    return false;
  }

  return OFF_NUTRIMENT_FIELDS.some((field) => {
    const candidates = OFF_NUTRIMENT_CANDIDATE_KEYS[field] || buildNutrimentCandidateKeys(field);
    return candidates.some((key) => Object.prototype.hasOwnProperty.call(nutriments, key));
  }) || [
    'energy-kcal_100g',
    'energy_kcal_100g',
    'energy-kj_100g',
    'energy_kj_100g',
    'energy_100g',
    'energy-kcal_prepared_100g',
    'energy_kcal_prepared_100g',
    'energy-kj_prepared_100g',
    'energy_kj_prepared_100g',
    'energy_prepared_100g',
  ].some((key) => Object.prototype.hasOwnProperty.call(nutriments, key));
};

export { OFF_NUTRIMENT_FIELDS };
